// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * The guarantee is verified, not asserted.
 *
 * charsight promises two things about `cleaned.<ext>`, and it checks both against the bytes it is
 * about to hand you rather than trusting the code that produced them:
 *
 *  1. **Every approved code point is gone.** Re-scanning the output must not turn up a single
 *     occurrence of anything the policy said to remove.
 *  2. **Nothing else changed.** Reconstructing the input from the output by undoing the planned
 *     edits must reproduce the original buffer byte for byte — including the BOM, the line endings,
 *     the trailing whitespace and any sequence that is not well-formed UTF-8.
 *
 * If either check fails the download is refused with the reason. A tool whose whole pitch is "your
 * file comes back byte-identical except what you approved" cannot ship that file on a promise.
 */

import { bytesEqual, type Edit } from '../core/bytes';

export interface VerifyResult {
  ok: boolean;
  /** Present when ok is false. Written for a human, not a log. */
  problem?: string;
}

/**
 * Reconstruct the input from the output by reversing each edit, and compare.
 *
 * This is a genuinely independent check: it walks the OUTPUT and the ORIGINAL simultaneously, so a
 * splicer bug that dropped an unrelated byte, mis-sorted an edit or got an offset wrong shows up
 * here as a mismatch, whereas re-running the splicer would reproduce the same bug and agree with
 * itself.
 */
export function verifyUntouchedBytes(
  original: Uint8Array,
  cleaned: Uint8Array,
  edits: readonly Edit[],
): VerifyResult {
  const sorted = [...edits].sort((a, b) => a.start - b.start);
  let read = 0;
  let write = 0;

  for (const e of sorted) {
    const gap = e.start - read;
    if (gap > 0) {
      if (
        write + gap > cleaned.length ||
        !bytesEqual(original.subarray(read, e.start), cleaned.subarray(write, write + gap))
      ) {
        return {
          ok: false,
          problem: `The bytes between offset ${read} and ${e.start}, which nothing asked to change, do not match the original.`,
        };
      }
      write += gap;
    }
    const rep = e.replacement;
    if (
      write + rep.length > cleaned.length ||
      !bytesEqual(rep, cleaned.subarray(write, write + rep.length))
    ) {
      return {
        ok: false,
        problem: `The replacement written at offset ${e.start} is not the one that was planned.`,
      };
    }
    write += rep.length;
    read = e.end;
  }

  const tail = original.length - read;
  if (
    write + tail !== cleaned.length ||
    !bytesEqual(original.subarray(read), cleaned.subarray(write))
  ) {
    return {
      ok: false,
      problem: `The end of the file, after the last change, does not match the original.`,
    };
  }
  return { ok: true };
}

/**
 * The check that is neither tautological nor blind: does the cleaned file DECODE to what it should?
 *
 * Comparing untouched byte ranges (above) can only fail if `memcpy` is broken, and checking that no
 * removed code point survives is blind to the one thing splicing can genuinely corrupt. That case
 * is not hypothetical: charsight promises to pass ill-formed bytes through untouched, so deleting a
 * well-formed span that happens to sit BETWEEN two ill-formed fragments joins them into a brand-new
 * valid code point — one that existed neither in the input nor in any finding. No byte was altered;
 * the file now says something different.
 *
 * So the real check re-decodes the output and compares the resulting code-point sequence against
 * the one predicted from the input minus the removed spans plus the replacements. If they differ,
 * the download is refused.
 */
export function verifyDecodedSequence(
  expected: readonly number[],
  actual: readonly number[],
): VerifyResult {
  if (expected.length !== actual.length) {
    return {
      ok: false,
      problem: `The cleaned file decodes to ${actual.length} characters where ${expected.length} were expected — removing a span appears to have changed how the bytes around it are read.`,
    };
  }
  for (let i = 0; i < expected.length; i++) {
    if (expected[i] !== actual[i]) {
      return {
        ok: false,
        problem: `Character ${i + 1} of the cleaned file decodes differently from what was planned — removing a span appears to have changed how the bytes around it are read.`,
      };
    }
  }
  return { ok: true };
}

/**
 * The code points the cleaned file is expected to contain, derived from the original units and the
 * planned edits. Units inside a removed span drop out; a replacement contributes its own.
 */
export function expectedCodePoints(
  units: readonly { cp: number; offset: number; length: number }[],
  edits: readonly Edit[],
  decodeReplacement: (bytes: Uint8Array) => number[],
): number[] {
  const sorted = [...edits].sort((a, b) => a.start - b.start);
  const out: number[] = [];
  let e = 0;
  for (const u of units) {
    while (e < sorted.length && sorted[e]!.end <= u.offset) {
      // A replacement whose span ended before this unit has already been emitted below.
      e++;
    }
    const edit = sorted[e];
    if (edit && u.offset >= edit.start && u.offset < edit.end) {
      if (u.offset === edit.start && edit.replacement.length) {
        out.push(...decodeReplacement(edit.replacement));
      }
      continue;
    }
    out.push(u.cp);
  }
  return out;
}
